import { useEffect, useMemo, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.css';
import './ProductDetail.css';
import ProductImages from './ProductImages'
import FlashDeals from '../Home/FlashDeals'
import { useNavigate, useLocation } from 'react-router-dom'
import { addToCart } from '../../store/thunk/addToCart';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { selectUserDetails } from '../../Selectors/Selector';
import { raiseError, response } from '../../store/actions/action';
import { checkAvailability } from '../../store/thunk/checkAvailablity';
import axios from 'axios';
import { getBaseUrl, loadStorage } from '../../Utils/Utils';

function ProductDetail() {
    const { id } = useParams();
    const { pathname } = useLocation();

    const userDetails = useSelector(selectUserDetails);
    const product = useSelector((store) => store.products.data);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [productQuantity, setProductQuantity] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [pincode, setPincode] = useState('');
    const [isAvailable, setIsAvailable] = useState(null);
    const [message, setMessage] = useState('');
    const [reviews, setReviews] = useState([]);
    const [reviewRating, setReviewRating] = useState(5);
    const [reviewComment, setReviewComment] = useState('');
    const [submittingReview, setSubmittingReview] = useState(false);
    const [editingReview, setEditingReview] = useState(false);
    const [editRating, setEditRating] = useState(5);
    const [editComment, setEditComment] = useState('');
    const [deletingReview, setDeletingReview] = useState(false);
    const [wishlist, setWishlist] = useState([]);
    const [wishlistLoading, setWishlistLoading] = useState(false);
    const [apiProduct, setApiProduct] = useState(null);

    const onClickBuyNow = (e) => {
        e.preventDefault();
        if (!userDetails) { dispatch(raiseError("Please login.")); return; }
        if (selectedProduct?.stockQuantity === 0) { dispatch(raiseError("This product is out of stock.")); return; }
        navigate(`/checkout/productId/${id}/quantity/${productQuantity}`);
    }
    const onClickCheckDeliveryAvailability = () => {
        if (!/^\d{6}$/.test(pincode.trim())) {
            setIsAvailable(false);
            setMessage('Please enter a valid 6-digit pincode.');
            return;
        }
        setMessage('Checking...');
        setIsAvailable(null);
        dispatch(checkAvailability(pincode.trim())).then((res) => {
            if (res.payload) {
                setIsAvailable(res.payload.isAvailable);
                setMessage(res.payload.message);
            } else {
                setIsAvailable(false);
                setMessage('Unable to check availability. Please try again.');
            }
        });
    }

    const selectedProduct = useMemo(() => {
        // apiProduct is fetched directly from API (has image + correct data)
        if (apiProduct) return apiProduct;
        // fallback: find in Redux store (id from useParams is a string, cast to number)
        if (product && product.length > 0) {
            return product.find((item) => item.id === Number(id));
        }
        return null;
    }, [product, id, apiProduct])

    const onClickAddToCart = (e) => {
        e.preventDefault();
        if (!userDetails) { dispatch(raiseError("Login is required for adding items in your cart.")); return; }
        if (selectedProduct?.stockQuantity === 0) { dispatch(raiseError("This product is out of stock.")); return; }
        setIsLoading(true);
        dispatch(addToCart({ UserId: userDetails.id, ProductId: Number(id), Quantity: productQuantity }))
            .finally(() => setIsLoading(false));
    }

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    // Fetch full product (with image) directly from API whenever id changes
    useEffect(() => {
        if (!id) return;
        setApiProduct(null);
        axios.get(`${getBaseUrl()}/products/${id}`)
            .then(res => setApiProduct(res.data))
            .catch(() => {});
    }, [id]);

    useEffect(() => {
        if (!id) return;
        axios.get(`${getBaseUrl()}/review/${id}`)
            .then(res => setReviews(res.data))
            .catch(() => {});
    }, [id]);

    useEffect(() => {
        if (!userDetails) return;
        const token = loadStorage("token");
        axios.get(`${getBaseUrl()}/wishlist`, { headers: { Authorization: `Bearer ${token}` } })
            .then(res => setWishlist(res.data.map(w => w.productId)))
            .catch(() => {});
    }, [userDetails]);

    const isWishlisted = wishlist.includes(Number(id));

    const toggleWishlist = async () => {
        if (!userDetails) { dispatch(raiseError("Please login to use wishlist.")); return; }
        const token = loadStorage("token");
        setWishlistLoading(true);
        try {
            if (isWishlisted) {
                await axios.delete(`${getBaseUrl()}/wishlist/${id}`, { headers: { Authorization: `Bearer ${token}` } });
                setWishlist(w => w.filter(pid => pid !== Number(id)));
            } else {
                await axios.post(`${getBaseUrl()}/wishlist/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
                setWishlist(w => [...w, Number(id)]);
            }
        } catch { } finally { setWishlistLoading(false); }
    };

    const myReview = userDetails ? reviews.find(r => Number(r.customerId) === Number(userDetails.id)) : null;

    const submitReview = async (e) => {
        e.preventDefault();
        if (!userDetails) { dispatch(raiseError("Please login to leave a review.")); return; }
        setSubmittingReview(true);
        try {
            const token = loadStorage("token");
            await axios.post(`${getBaseUrl()}/review`,
                { ProductId: Number(id), Rating: reviewRating, Comment: reviewComment },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const res = await axios.get(`${getBaseUrl()}/review/${id}`);
            setReviews(res.data);
            setReviewComment('');
            setReviewRating(5);
            dispatch(response("Review submitted!"));
        } catch {
            dispatch(raiseError("Failed to submit review."));
        } finally { setSubmittingReview(false); }
    };

    const startEditReview = () => {
        setEditRating(myReview.rating);
        setEditComment(myReview.comment);
        setEditingReview(true);
    };

    const saveEditReview = async (e) => {
        e.preventDefault();
        setSubmittingReview(true);
        try {
            const token = loadStorage("token");
            await axios.put(`${getBaseUrl()}/review/${myReview.id}`,
                { Rating: editRating, Comment: editComment },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const res = await axios.get(`${getBaseUrl()}/review/${id}`);
            setReviews(res.data);
            setEditingReview(false);
            dispatch(response("Review updated!"));
        } catch {
            dispatch(raiseError("Failed to update review."));
        } finally { setSubmittingReview(false); }
    };

    const deleteReview = async () => {
        setDeletingReview(true);
        try {
            const token = loadStorage("token");
            await axios.delete(`${getBaseUrl()}/review/${myReview.id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const res = await axios.get(`${getBaseUrl()}/review/${id}`);
            setReviews(res.data);
            setEditingReview(false);
            dispatch(response("Review deleted."));
        } catch {
            dispatch(raiseError("Failed to delete review."));
        } finally { setDeletingReview(false); }
    };

    const avgRating = reviews.length > 0
        ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
        : null;
    const outOfStock = selectedProduct?.stockQuantity === 0;

    return (
        <div className="pd-page">
            <div className="container">

                {/* ── Top row: gallery | info | sidebar ── */}
                <div className="row g-4">

                    {/* Image gallery */}
                    <div className="col-lg-4">
                        <div className="pd-gallery">
                            <div className="pd-gallery__main">
                                <ProductImages image={selectedProduct?.image} />
                            </div>
                            <div className="pd-gallery__thumbs">
                                {[0,1,2].map(i => (
                                    <div key={i} className={`pd-gallery__thumb${i === 0 ? ' active' : ''}`}>
                                        <ProductImages image={selectedProduct?.image} />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Product info */}
                    <div className="col-lg-5">
                        <div className="pd-info">

                            <div className="pd-title-row">
                                <h1 className="pd-title">{selectedProduct?.name || 'Loading...'}</h1>
                                <button
                                    className={`pd-wishlist-btn${isWishlisted ? ' wishlisted' : ''}`}
                                    onClick={toggleWishlist}
                                    disabled={wishlistLoading}
                                    title={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
                                >
                                    <svg width="17" height="17" viewBox="0 0 24 24" fill={isWishlisted ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                                    </svg>
                                </button>
                            </div>

                            {/* Stars */}
                            <div className="pd-stars">
                                {[1,2,3,4,5].map(s => {
                                    const diff = avgRating ? parseFloat(avgRating) - s : -1;
                                    return (
                                        <svg key={s} width="15" height="15" viewBox="0 0 24 24"
                                            fill={diff >= 0 ? '#f59e0b' : diff >= -0.5 ? 'url(#half)' : 'none'}
                                            stroke="#f59e0b" strokeWidth="1.5">
                                            <defs>
                                                <linearGradient id="half"><stop offset="50%" stopColor="#f59e0b"/><stop offset="50%" stopColor="transparent"/></linearGradient>
                                            </defs>
                                            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                                        </svg>
                                    );
                                })}
                                <span className="pd-stars__meta">
                                    {avgRating ? `${avgRating} · ${reviews.length} review${reviews.length !== 1 ? 's' : ''}` : 'No reviews yet'}
                                </span>
                            </div>

                            {/* Price */}
                            <div className="pd-price">
                                <span className="pd-price__symbol">₹</span>
                                {selectedProduct?.price}
                            </div>

                            {/* Badges */}
                            <div className="pd-badges">
                                {selectedProduct?.category && (
                                    <span className="pd-badge pd-badge--cat">
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/></svg>
                                        {selectedProduct.category}
                                    </span>
                                )}
                                {outOfStock
                                    ? <span className="pd-badge pd-badge--out">Out of Stock</span>
                                    : selectedProduct?.stockQuantity < 10
                                        ? <span className="pd-badge pd-badge--low">Only {selectedProduct.stockQuantity} left</span>
                                        : <span className="pd-badge pd-badge--ok">
                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8"><polyline points="20 6 9 17 4 12"/></svg>
                                            In Stock
                                          </span>
                                }
                                {selectedProduct?.requiresPrescription && (
                                    <span className="pd-badge pd-badge--rx">
                                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                                        Rx Required
                                    </span>
                                )}
                            </div>

                            <div className="pd-divider" />

                            {/* Qty + Add to Cart */}
                            <div className="pd-qty-row">
                                <span className="pd-qty-label">Qty</span>
                                <div className="pd-qty">
                                    <button className="pd-qty__btn" onClick={() => setProductQuantity(q => q > 1 ? q - 1 : 1)} disabled={outOfStock}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                    </button>
                                    <span className="pd-qty__val">{productQuantity}</span>
                                    <button className="pd-qty__btn" disabled={outOfStock} onClick={() => setProductQuantity(q => { const max = selectedProduct?.stockQuantity ?? 99; return q < max ? q + 1 : q; })}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                                    </button>
                                </div>
                            </div>

                            <div className="pd-actions">
                                <button className="pd-btn-cart" disabled={outOfStock || isLoading} onClick={onClickAddToCart}>
                                    {isLoading
                                        ? <span className="spinner-border spinner-border-sm" />
                                        : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
                                    }
                                    Add to Cart
                                </button>
                            </div>

                            <button className="pd-btn-buy" disabled={outOfStock} onClick={onClickBuyNow}>
                                {outOfStock ? 'Out of Stock' : 'Buy Now '} 
                                {/* — ₹' + selectedProduct?.price} */}
                            </button>

                        </div>
                    </div>

                    {/* Sidebar */}
                    <div className="col-lg-3">

                        {/* Delivery options */}
                        {(selectedProduct?.freeShipping || selectedProduct?.cashOnDelivery || selectedProduct?.returns14Day || selectedProduct?.shippingCharge > 0 || selectedProduct?.codCharge > 0) && (
                        <div className="pd-card">
                            <p className="pd-card__title">Delivery Options</p>
                            {selectedProduct?.freeShipping ? (
                            <div className="pd-delivery-row">
                                <div className="pd-delivery-icon">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="3" width="15" height="13"/><path d="M16 8h4l3 5v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                                </div>
                                <div className="pd-delivery-text">
                                    <strong>Free Shipping</strong>
                                    <small>On all orders</small>
                                </div>
                            </div>
                            ) : selectedProduct?.shippingCharge > 0 ? (
                            <div className="pd-delivery-row">
                                <div className="pd-delivery-icon">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="3" width="15" height="13"/><path d="M16 8h4l3 5v3h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                                </div>
                                <div className="pd-delivery-text">
                                    <strong>Shipping Available</strong>
                                    <small>₹{selectedProduct.shippingCharge} delivery charge</small>
                                </div>
                            </div>
                            ) : null}
                            {selectedProduct?.cashOnDelivery ? (
                            <div className="pd-delivery-row">
                                <div className="pd-delivery-icon">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
                                </div>
                                <div className="pd-delivery-text">
                                    <strong>Cash on Delivery</strong>
                                    <small>Available at checkout</small>
                                </div>
                            </div>
                            ) : selectedProduct?.codCharge > 0 ? (
                            <div className="pd-delivery-row">
                                <div className="pd-delivery-icon">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
                                </div>
                                <div className="pd-delivery-text">
                                    <strong>Cash on Delivery</strong>
                                    <small>₹{selectedProduct.codCharge} extra charge</small>
                                </div>
                            </div>
                            ) : null}
                            {selectedProduct?.returns14Day && (
                            <div className="pd-delivery-row">
                                <div className="pd-delivery-icon">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-4.95"/></svg>
                                </div>
                                <div className="pd-delivery-text">
                                    <strong>14-Day Returns</strong>
                                    <small>Hassle-free returns</small>
                                </div>
                            </div>
                            )}
                        </div>
                        )}

                        {/* Highlights */}
                        {selectedProduct?.description && (
                            <div className="pd-card">
                                <p className="pd-card__title">Product Highlights</p>
                                <ul className="pd-highlights">
                                    {selectedProduct.description
                                        .replace(/<[^>]*>/g, ' ')
                                        .replace(/&nbsp;/g, ' ')
                                        .replace(/&amp;/g, '&')
                                        .replace(/&lt;/g, '<')
                                        .replace(/&gt;/g, '>')
                                        .split(/[.\n]/)
                                        .filter(s => s.trim().length > 4)
                                        .slice(0, 5)
                                        .map((point, i) => (
                                            <li key={i}>{point.trim()}</li>
                                        ))}
                                </ul>
                            </div>
                        )}

                        {/* Pincode checker */}
                        <div className="pd-card">
                            <p className="pd-card__title">Delivery Availability</p>
                            <div className="pd-pincode">
                                <svg className="pd-pincode__loc" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                                <input
                                    className="pd-pincode__input"
                                    type="text"
                                    placeholder="Enter 6-digit pincode"
                                    maxLength={6}
                                    value={pincode}
                                    onChange={e => setPincode(e.target.value.replace(/\D/g,''))}
                                    onKeyDown={e => e.key === 'Enter' && onClickCheckDeliveryAvailability()}
                                />
                                <button className="pd-pincode__check" onClick={onClickCheckDeliveryAvailability}>Check</button>
                            </div>
                            {message && (
                                <p className={`pd-pin-msg ${isAvailable === true ? 'pd-pin-msg--ok' : isAvailable === false ? 'pd-pin-msg--err' : 'pd-pin-msg--chk'}`}>
                                    {isAvailable === true && '✓ '}{isAvailable === false && '✗ '}{message}
                                </p>
                            )}
                        </div>

                    </div>
                </div>

                {/* ── Lower section: description / info / reviews ── */}
                <div className="pd-lower">

                    {/* Description */}
                    <h2 className="pd-lower__h">Description</h2>
                    {selectedProduct?.description
                        ? <div
                            className="pd-description-html"
                            style={{ color:'#374151', lineHeight:1.75, fontSize:'.9rem', marginBottom:28 }}
                            dangerouslySetInnerHTML={{ __html: selectedProduct.description }}
                          />
                        : <p style={{ color:'#374151', lineHeight:1.75, fontSize:'.9rem', marginBottom:28 }}>No description available for this product.</p>
                    }

                    {/* Additional info table */}
                    {(selectedProduct?.numberOfServings || selectedProduct?.servingSize || selectedProduct?.proteinPerServing || selectedProduct?.vegetarianType || selectedProduct?.batchNumber || selectedProduct?.expiryDate) && (
                        <>
                            <h2 className="pd-lower__h">Additional Information</h2>
                            <table className="table table-sm mb-4" style={{ maxWidth:480, fontSize:'.88rem' }}>
                                <tbody>
                                    {selectedProduct?.numberOfServings && <tr><td className="text-muted" style={{width:'50%'}}>Number of Servings</td><td className="font-weight-bold">{selectedProduct.numberOfServings}</td></tr>}
                                    {selectedProduct?.servingSize      && <tr><td className="text-muted">Serving Size</td><td className="font-weight-bold">{selectedProduct.servingSize}</td></tr>}
                                    {selectedProduct?.proteinPerServing && <tr><td className="text-muted">Protein per Serving</td><td className="font-weight-bold">{selectedProduct.proteinPerServing}</td></tr>}
                                    {selectedProduct?.vegetarianType   && <tr><td className="text-muted">Type</td><td className="font-weight-bold">{selectedProduct.vegetarianType}</td></tr>}
                                    {selectedProduct?.batchNumber      && <tr><td className="text-muted">Batch Number</td><td className="font-weight-bold">{selectedProduct.batchNumber}</td></tr>}
                                    {selectedProduct?.expiryDate       && <tr><td className="text-muted">Expiry Date</td><td className="font-weight-bold">{new Date(selectedProduct.expiryDate).toLocaleDateString('en-IN',{month:'long',year:'numeric'})}</td></tr>}
                                </tbody>
                            </table>
                        </>
                    )}

                    {/* Reviews */}
                    <h2 className="pd-lower__h">
                        Reviews
                        {avgRating && <span style={{ fontSize:'.8rem', fontWeight:400, color:'#94a3b8', marginLeft:10 }}>{avgRating} avg · {reviews.length} review{reviews.length !== 1 ? 's' : ''}</span>}
                    </h2>

                    {reviews.length === 0 && <p style={{ color:'#94a3b8', fontSize:'.88rem' }}>No reviews yet. Be the first to share your experience!</p>}

                    <div className="row">
                        {reviews.map(rev => {
                            const isOwn = userDetails && Number(rev.customerId) === Number(userDetails.id);
                            return (
                                <div className="col-lg-6" key={rev.id}>
                                    <div className={`pd-review${isOwn ? ' pd-review--own' : ''}`}>
                                        {isOwn && editingReview ? (
                                            <form onSubmit={saveEditReview}>
                                                <div className="pd-star-select mb-2">
                                                    {[1,2,3,4,5].map(s => (
                                                        <button key={s} type="button" className={s <= editRating ? 'lit' : ''} onClick={() => setEditRating(s)}>★</button>
                                                    ))}
                                                </div>
                                                <textarea className="form-control form-control-sm mb-2" rows={3} value={editComment} onChange={e => setEditComment(e.target.value)} required />
                                                <div className="pd-review__actions">
                                                    <button className="pd-review__btn pd-review__btn--edit" type="submit" disabled={submittingReview}>{submittingReview ? 'Saving…' : 'Save Changes'}</button>
                                                    <button className="pd-review__btn pd-review__btn--del" type="button" onClick={() => setEditingReview(false)}>Cancel</button>
                                                </div>
                                            </form>
                                        ) : (
                                            <>
                                                <div className="pd-review__header">
                                                    <div className="pd-review__stars">
                                                        {[1,2,3,4,5].map(s => <span key={s}>{s <= rev.rating ? '★' : '☆'}</span>)}
                                                    </div>
                                                    <span className="pd-review__name">{rev.customerName}</span>
                                                    {isOwn && <span className="pd-review__badge">Your Review</span>}
                                                    <span className="pd-review__date">{new Date(rev.createdAt).toLocaleDateString('en-IN')}</span>
                                                </div>
                                                <p className="pd-review__text">{rev.comment}</p>
                                                {isOwn && (
                                                    <div className="pd-review__actions">
                                                        <button className="pd-review__btn pd-review__btn--edit" onClick={startEditReview}>Edit</button>
                                                        <button className="pd-review__btn pd-review__btn--del" onClick={deleteReview} disabled={deletingReview}>{deletingReview ? '…' : 'Delete'}</button>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Leave review form */}
                    {!myReview ? (
                        <div className="pd-review-form">
                            <p className="pd-review-form__title">Share Your Experience</p>
                            <form onSubmit={submitReview}>
                                <div className="pd-star-select">
                                    {[1,2,3,4,5].map(s => (
                                        <button key={s} type="button" className={s <= reviewRating ? 'lit' : ''} onClick={() => setReviewRating(s)}>★</button>
                                    ))}
                                </div>
                                <textarea className="form-control mb-3" rows={3} placeholder="Write your review…" value={reviewComment} onChange={e => setReviewComment(e.target.value)} required style={{ borderRadius:10, fontSize:'.88rem' }} />
                                <button className="pd-btn-buy" type="submit" disabled={submittingReview} style={{ maxWidth:200 }}>
                                    {submittingReview ? 'Submitting…' : 'Submit Review'}
                                </button>
                            </form>
                        </div>
                    ) : (
                        <p style={{ color:'#94a3b8', fontSize:'.82rem', marginTop:12 }}>You've already reviewed this product — edit or delete your review above.</p>
                    )}

                </div>

                {/* Related products */}
                <div style={{ marginTop:40 }}>
                    <div className="text-center mb-4">
                        <h2 className="pd-related-heading">You May Also Like</h2>
                        <p className="pd-related-sub">Handpicked products based on your interest</p>
                    </div>
                    <div className="slider arrow-light slider-gap" data-slides-to-show="6" data-autoplay="true" data-nav="true" data-dots="false">
                        <FlashDeals />
                    </div>
                </div>

            </div>
        </div>
    );
}
export default ProductDetail;